// CashflowLedger.js — Table of all cashflow events with CSV export button.

import React from 'react';
import { Download } from 'lucide-react';

const EVENT_LABELS = {
  fx_conversion_in: 'FX Conversion In',
  fx_conversion_out: 'FX Conversion Out',
  auction_settlement: 'Auction Settlement',
  maturity: 'Maturity',
  wht: 'Withholding Tax',
  fees: 'Fees',
  interest_withdrawal: 'Interest Withdrawal',
  rollover: 'Rollover',
};

const EVENT_COLORS = {
  fx_conversion_in: 'text-blue-600',
  fx_conversion_out: 'text-blue-600',
  auction_settlement: 'text-amber-600',
  maturity: 'text-green-600',
  wht: 'text-red-600',
  fees: 'text-red-600',
  interest_withdrawal: 'text-purple-600',
  rollover: 'text-indigo-600',
};

function formatAmount(value) {
  if (value == null) return '';
  const sign = value < 0 ? '-' : '+';
  return `${sign}${Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

export default function CashflowLedger({ cashflows = [], sourceCurrency = 'USD', runId }) {
  const handleExport = () => {
    if (runId) {
      // Use API endpoint for export
      window.open(`${process.env.REACT_APP_API_URL || '/api/v1'}/simulator/investor/runs/${runId}/csv`, '_blank');
      return;
    }

    // Client-side CSV generation
    const headers = ['Date', 'Event', 'Amount (KES)', `Amount (${sourceCurrency})`, 'FX Rate', 'Description'];
    const rows = cashflows.map(cf => [
      cf.event_date,
      EVENT_LABELS[cf.event_type] || cf.event_type,
      cf.amount_local != null ? cf.amount_local.toFixed(2) : '',
      cf.amount_base != null ? cf.amount_base.toFixed(2) : '',
      cf.fx_rate != null ? cf.fx_rate.toFixed(4) : '',
      `"${(cf.description || '').replace(/"/g, '""')}"`,
    ].join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `simulation-cashflows.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!cashflows || cashflows.length === 0) {
    return <p className="text-sm text-gray-400 py-4">No cashflow events</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{cashflows.length} events</span>
        <button
          onClick={handleExport}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-4 py-2 text-xs text-gray-500 uppercase font-medium">Date</th>
              <th className="text-left px-4 py-2 text-xs text-gray-500 uppercase font-medium">Event</th>
              <th className="text-right px-4 py-2 text-xs text-gray-500 uppercase font-medium">Amount (KES)</th>
              <th className="text-right px-4 py-2 text-xs text-gray-500 uppercase font-medium">Amount ({sourceCurrency})</th>
              <th className="text-right px-4 py-2 text-xs text-gray-500 uppercase font-medium">FX Rate</th>
              <th className="text-left px-4 py-2 text-xs text-gray-500 uppercase font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {cashflows.map((cf, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="px-4 py-2 text-gray-700 font-mono text-xs whitespace-nowrap">
                  {cf.event_date}
                </td>
                <td className={`px-4 py-2 font-medium ${EVENT_COLORS[cf.event_type] || 'text-gray-600'}`}>
                  {EVENT_LABELS[cf.event_type] || cf.event_type}
                </td>
                <td className={`px-4 py-2 text-right font-mono ${cf.amount_local < 0 ? 'text-red-600' : cf.amount_local > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                  {cf.amount_local != null ? formatAmount(cf.amount_local) : ''}
                </td>
                <td className={`px-4 py-2 text-right font-mono ${cf.amount_base < 0 ? 'text-red-600' : cf.amount_base > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                  {cf.amount_base != null ? formatAmount(cf.amount_base) : ''}
                </td>
                <td className="px-4 py-2 text-right font-mono text-gray-500">
                  {cf.fx_rate != null ? cf.fx_rate.toFixed(4) : ''}
                </td>
                <td className="px-4 py-2 text-gray-500 text-xs max-w-xs truncate">
                  {cf.description}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
